import { BaseApiClient } from "../base-api-client";
export class PositionClient extends BaseApiClient {
    constructor(config) {
        const fullConfig = {
            ...config,
            baseURL: `${config.baseURL}/trading/positions`,
        };
        super(fullConfig, "PositionClient");
    }
    async getOpenPositions(userId, exchangeCode) {
        try {
            const query = exchangeCode
                ? `?exchangeCode=${encodeURIComponent(exchangeCode)}`
                : "";
            const response = await this.get(`/${userId}${query}`);
            if (!response.success) {
                throw new Error(response.error || "Failed to get open positions");
            }
            return response.data?.positions || [];
        }
        catch (error) {
            console.error("[PositionClient] Error getting open positions:", this.truncateError(error));
            return [];
        }
    }
    async getClosedPositions(userId, limit = 10, skip = 0, exchangeCode) {
        try {
            const exchangeQuery = exchangeCode
                ? `&exchangeCode=${encodeURIComponent(exchangeCode)}`
                : "";
            const response = await this.get(`/closed/${userId}?limit=${limit}&skip=${skip}${exchangeQuery}`);
            if (!response.success) {
                throw new Error(response.error || "Failed to get closed positions");
            }
            return (response.data || {
                positions: [],
                pagination: { total: 0, limit, skip, hasMore: false },
            });
        }
        catch (error) {
            console.error("[PositionClient] Error getting closed positions:", this.truncateError(error));
            return {
                positions: [],
                pagination: { total: 0, limit, skip, hasMore: false },
            };
        }
    }
    async getPositionById(userId, positionId) {
        try {
            const response = await this.post("/get-by-id", {
                userId,
                positionId,
            });
            if (!response.success) {
                return null;
            }
            return response.data?.position || null;
        }
        catch (error) {
            console.error("[PositionClient] Error getting position by ID:", this.truncateError(error));
            return null;
        }
    }
    async closePosition(payload) {
        try {
            const response = await this.post("/close", payload);
            if (!response.success) {
                throw new Error(response.error || "Failed to close position");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[PositionClient] Error closing position:", this.truncateError(error));
            throw error;
        }
    }
    async getPositionProtection(payload) {
        const response = await this.post("/tpsl/get", payload);
        if (!response.success || !response.data) {
            throw new Error(response.error || "Could not read TP/SL");
        }
        return response.data;
    }
    async setPositionProtection(payload) {
        const response = await this.post("/tpsl", payload);
        if (!response.success || !response.data) {
            throw new Error(response.error || "Could not change TP/SL");
        }
        return response.data;
    }
    async openPosition(payload) {
        try {
            const response = await this.post("/open", payload);
            if (!response.success) {
                throw new Error(response.error || "Failed to open position");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[PositionClient] Error opening position:", this.truncateError(error));
            throw error;
        }
    }
}
export class MarketDataClient extends BaseApiClient {
    constructor(config) {
        const fullConfig = {
            ...config,
            baseURL: `${config.baseURL}/trading/market`,
        };
        super(fullConfig, "MarketDataClient");
    }
    async getTokenData(userId, symbol, exchangeCode) {
        try {
            const query = exchangeCode
                ? `?exchangeCode=${encodeURIComponent(exchangeCode)}`
                : "";
            const response = await this.get(`/token/${userId}/${symbol}${query}`);
            if (!response.success) {
                throw new Error(response.error || "Failed to get token data");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting token data:", this.truncateError(error));
            return null;
        }
    }
    async getTokenBalance(userId, symbol) {
        try {
            const response = await this.get(`/balance/${userId}/${symbol}`);
            if (!response.success) {
                throw new Error(response.error || "Failed to get token balance");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting token balance:", this.truncateError(error));
            return null;
        }
    }
    async get24hTicker(userId, symbol, exchangeCode) {
        try {
            const query = exchangeCode
                ? `?exchangeCode=${encodeURIComponent(exchangeCode)}`
                : "";
            const response = await this.get(`/ticker-24h/${userId}/${symbol}${query}`);
            if (!response.success) {
                throw new Error(response.error || "Failed to get 24h ticker");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting 24h ticker:", this.truncateError(error));
            return null;
        }
    }
    async getSymbolInfo(userId, symbol, exchangeCode) {
        try {
            const response = await this.post("/symbol/info", {
                userId,
                symbol,
                exchangeCode,
            });
            if (!response.success) {
                throw new Error(response.error || "Failed to get symbol info");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting symbol info:", this.truncateError(error));
            return null;
        }
    }
    async getTokenAvailability(userId, symbol) {
        try {
            const response = await this.post("/token/availability", {
                userId,
                symbol,
            });
            if (!response.success) {
                throw new Error(response.error || "Failed to get token availability");
            }
            return response.data || null;
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting token availability:", this.truncateError(error));
            return null;
        }
    }
    async getPriceChanges(userId, symbol, timestamp, interval = "1m", limit = 15) {
        try {
            const response = await this.post("/price-changes", {
                userId,
                symbol,
                timestamp,
                interval,
                limit,
            });
            if (!response.success) {
                throw new Error(response.error || "Failed to get price changes");
            }
            return (response.data?.data?.map((data) => ({
                startTime: data.timestamp,
                openPrice: data.open.toString(),
                highPrice: data.high.toString(),
                lowPrice: data.low.toString(),
                closePrice: data.close.toString(),
                volume: data.volume.toString(),
                turnover: data.turnover.toString(),
            })) || []);
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting price changes:", this.truncateError(error));
            return [];
        }
    }
    async getPriceChangesForPeriod(userId, symbol, startTime, endTime, interval = "1m", padding = 10) {
        try {
            const response = await this.post("/price-changes-period", {
                userId,
                symbol,
                startTime,
                endTime,
                interval,
                padding,
            });
            if (!response.success) {
                throw new Error(response.error || "Failed to get price changes");
            }
            return (response.data?.data?.map((data) => ({
                startTime: data.timestamp,
                openPrice: data.open.toString(),
                highPrice: data.high.toString(),
                lowPrice: data.low.toString(),
                closePrice: data.close.toString(),
                volume: data.volume.toString(),
                turnover: data.turnover.toString(),
            })) || []);
        }
        catch (error) {
            console.error("[MarketDataClient] Error getting price changes for period:", this.truncateError(error));
            return [];
        }
    }
}
//# sourceMappingURL=position-client.js.map